/**
 * CreateWatchlistModalContainer — canonical create-watchlist dialog
 * (SE-5007).
 *
 * Single source of truth for "Create a new watchlist" across the three
 * entry points called out in Notion section 3.1 of the SRD epic:
 *
 *   1. See all lists drawer (SE-5003 — caller wires it)
 *   2. Add Player Path B inline create
 *   3. Player page "+ Add to Watchlist" dropdown
 *
 * Behaviour:
 * - Submit is disabled until the input has at least one non-whitespace char
 *   or while the mutation is in flight.
 * - On success: emits a "List '<name>' created" toast, then either invokes
 *   the optional `onCreated` callback (Path B inline) or activates the new
 *   list pill on the Watchlists tab (`watchListSelectedVar` + router push
 *   to `/watchlist/<id>`).
 * - On cancel / ESC / backdrop: resets the input and calls `onClose`.
 * @module components/CreateWatchlistModal/CreateWatchlistModalContainer
 */

import { useRouter } from "expo-router";
import React, { useCallback, useState } from "react";
import { Platform } from "react-native";

import CreateWatchlistModalView from "./CreateWatchlistModalView";

import { AnalyticsTracker } from "@/features/analytics";
import { useCurrentUser } from "@/features/auth";
import {
  PlayerSearchWatchlistMetaFragment,
  usePlayerSearchCreateWatchlistMutation,
} from "@/generated/graphql";
import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";
import { useToastNotification } from "@/hooks/useToastNotification";
import { watchListSelectedVar } from "@/stores/player";
import { ifWeb } from "@/utils/platform";

/** Public props for the canonical create-watchlist modal */
export interface CreateWatchlistModalProps {
  /** Whether the modal is open */
  readonly isOpen: boolean;
  /** Fires when the dialog is dismissed (Cancel / ESC / backdrop / post-create) */
  readonly onClose: () => void;
  /**
   * When provided, called with the newly created watchlist instead of the
   * default navigation. Use this for inline flows (e.g. Add Player Path B)
   * where the caller wants the new list as a destination without leaving
   * the current screen.
   */
  readonly onCreated?: (watchlist: PlayerSearchWatchlistMetaFragment) => void;
}

/**
 * Renders the canonical create-watchlist dialog with its mutation,
 * toast, and navigation side effects.
 * @param props - Component props
 * @param props.isOpen - Whether the modal is open
 * @param props.onClose - Fires when the dialog is dismissed
 * @param props.onCreated - Optional inline callback (skips navigation)
 */
const CreateWatchlistModalContainer = ({
  isOpen,
  onClose,
  onCreated,
}: CreateWatchlistModalProps) => {
  const router = useRouter();
  const toast = useToastNotification();
  const currentUser = useCurrentUser();
  const recordCreateWatchlist = useRecordUserGoal({
    goalType: GoalType.CreateWatchlist,
    value: "",
  });

  const [name, setName] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  const [createWatchlist, { loading }] = usePlayerSearchCreateWatchlistMutation(
    {
      // Refetch the lists queries in the background so the new list
      // appears in the sidebar / drawer without blocking the modal's
      // close handshake.
      refetchQueries: [
        "WatchlistListMyUserWatchlists",
        "ScoutingListUserWatchlists",
      ],
      notifyOnNetworkStatusChange: true,
    }
  );

  const handleClose = useCallback(() => {
    if (loading) return;
    setName("");
    setErrorMessage(undefined);
    onClose();
  }, [loading, onClose]);

  /**
   * Backdrop / ESC dismiss handler. Per Notion 3.1, mobile must NOT dismiss
   * on tap-outside to prevent accidental loss of typed input while the
   * keyboard is open — only the Cancel button dismisses on that breakpoint.
   * Desktop honours both backdrop and ESC.
   */
  const handleBackdropDismiss = useCallback(() => {
    if (loading) return;
    if (Platform.OS !== "web") return;
    handleClose();
  }, [loading, handleClose]);

  const handleSubmit = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed || loading) return;
    setErrorMessage(undefined);

    const result = await createWatchlist({
      variables: { input: { name: trimmed } },
    }).catch((mutationError: unknown) => {
      const message =
        mutationError instanceof Error
          ? mutationError.message
          : "Could not create the list. Please try again.";
      setErrorMessage(message);
      return undefined;
    });
    if (!result) return;
    const created = result.data?.createWatchlist;
    if (!created) {
      setErrorMessage("Could not create the list. Please try again.");
      return;
    }

    recordCreateWatchlist({
      value: created.id,
      goalType: GoalType.CreateWatchlist,
    });
    AnalyticsTracker.user.logWatchlistCreated({
      user_id: currentUser?.id || "",
      organization_id: currentUser?.organizationId || "",
      phone_number: currentUser?.phoneNumber || "",
      watchlist_id: created.id,
      watchlist_name: created.name,
      action: "created",
      platform: ifWeb("web", "mobile"),
      timestamp: new Date().toISOString(),
    });

    toast.show({
      description: `List '${created.name}' created`,
      action: "success",
    });

    setName("");
    setErrorMessage(undefined);
    onClose();

    if (onCreated) {
      onCreated(created);
      return;
    }
    // Default flow: activate the new list pill on the Watchlists tab
    // and land on its (empty) state.
    watchListSelectedVar(created.id);
    // Watchlist routes use a single `/watchlist` page driven by the
    // `watchListSelectedVar` reactive variable rather than a URL param.
    router.push("/watchlist");
  }, [
    name,
    loading,
    createWatchlist,
    recordCreateWatchlist,
    currentUser?.id,
    currentUser?.organizationId,
    currentUser?.phoneNumber,
    toast,
    onClose,
    onCreated,
    router,
  ]);

  const canSubmit = name.trim().length > 0 && !loading;

  const handleChangeName = useCallback((value: string) => {
    setName(value);
    setErrorMessage(undefined);
  }, []);

  return (
    <CreateWatchlistModalView
      isOpen={isOpen}
      name={name}
      isLoading={loading}
      canSubmit={canSubmit}
      errorMessage={errorMessage}
      onChangeName={handleChangeName}
      onSubmit={handleSubmit}
      onClose={handleClose}
      onBackdropDismiss={handleBackdropDismiss}
    />
  );
};

CreateWatchlistModalContainer.displayName = "CreateWatchlistModalContainer";

export default CreateWatchlistModalContainer;
